import { Grid } from '../../../../../Common/Grid';
import { Button, Input, Typography } from '@fullscreendigital/carrefour-dex-fe';
import { TitleHeader } from '../../../../Common/TitleHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { URLS } from '../../../../constants';
import { useQuery } from 'react-query';
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../../utils/api';
import { formatErrorMessage } from '../../../../../../../utils/formatting';
import { useNotification } from '../../../../../../../hooks';
import React, { useMemo, useState } from 'react';
import { getValidationSchema, initialValues } from './helpers';
import { useFormik } from 'formik';
import AlertModal from '../../../../Common/AlertModal/AlertModal';
import { CircularProgress, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Paper } from '@mui/material';

const ExpectedReturnDetailStep2 = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [isInvalidTruckNumberModalOpen, setIsInvalidTruckNumberModalOpen] = useState(false);
  const [InvalidTruckNumberModalContent, setInvalidTruckNumberModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [params] = useSearchParams();
  const parentId = Number(params.get('id'));

  const validationSchema = useMemo(() => getValidationSchema(), []);
  const formik = useFormik({
    enableReinitialize: false,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      let message = 'Există diferențe între cantitatea aşteptată şi cea descărcată pentru ';
      let continueSubmit = true;

      values.stackList.map((stack: any) => {
        if (stack.stackNumber != stack.confirmedStackNumber) {
          message += ' numărul de stive la ' + stack.shopName + ', ';
          continueSubmit = false;
          openInvalidTruckNumberModal(message);
        }
      });

      continueSubmit && (await onContinueSubmit());
    }
  });

  const { data: truck, isLoading } = useQuery(
    'stackList',
    async () => {
      const truck = await makeCoreGetRequest(
        '/shop-reception-mobile/truck/hub/exit/stacks/' + parentId,
        null
      );
      const newStackList = truck.stackList.map((stack, index) => ({
        ...stack,
        id: index
      }));
      await formik.setValues({
        stackList: newStackList
      });
      return truck;
    },
    {
      initialData: [],
      onError: error => {
        showNotification({
          message: `Error while fetching truck. ${formatErrorMessage(error)}`,
          type: 'error'
        });
      }
    }
  );

  const onContinueSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const stackList = formik.values.stackList.map(({ id, ...rest }) => rest);
      const payload = {
        id: truck.id,
        truckNumber: truck.truckNumber,
        driverName: truck.driverName,
        shopNamesAndCodes: truck.shopNamesAndCodes,
        stackList: stackList,
        nextTruckId: truck.nextTruckId
      };
      await makeCorePostRequest('/shop-reception-mobile/truck/hub/exit/confirm-stacks', payload, null);
      navigate(
        `${URLS.AUTH_EXPECTED_RETURN_DETAIL_STEP_3}?truckId=${truck.nextTruckId}&parentId=${parentId}`
      );
    } catch (error) {
      showNotification({
        message: `Error while submitting. ${formatErrorMessage(error)}`,
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openInvalidTruckNumberModal = async message => {
    setInvalidTruckNumberModalContent({
      title: 'Atenție!',
      description: message,
      buttons: [
        {
          label: 'Mai verific',
          onClick: () => setIsInvalidTruckNumberModalOpen(false),
          color: 'primary'
        },
        {
          label: 'Am terminat',
          onClick: async () => {
            await onContinueSubmit();
            setIsInvalidTruckNumberModalOpen(false);
          },
          color: 'primary',
          disabled: isSubmitting
        }
      ]
    });
    setIsInvalidTruckNumberModalOpen(true);
  };

  const closeInvalidTruckNumberModal = () => {
    setIsInvalidTruckNumberModalOpen(false);
  };

  if (isLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />;
  }

  const rows = formik.values.stackList.map((stack: any) => ({
    id: stack.id,
    shopName: stack.shopName,
    confirmedStackNumber: stack.confirmedStackNumber
  }));

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <TitleHeader title="RETURURI AȘTEPTATE CAMION" />,
        <>
          {truck.truckNumber && truck.driverName && (
            <Typography variant="medium" component="p">
              {truck.truckNumber} / {truck.driverName}
            </Typography>
          )}
          <Typography variant="medium" component="p">
            {truck.shopNamesAndCodes}
          </Typography>
        </>,
        <Typography variant="medium" component="h4">
          Descărcare stive
        </Typography>,
        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ background: '#0970e6' }}>
              <TableRow>
                <TableCell align="left" style={{fontFamily: 'Ubuntu', color: '#f8f9fb', fontWeight: 'bold'}}>MAGAZIN</TableCell>
                <TableCell align="center" style={{fontFamily: 'Ubuntu', color: '#f8f9fb', fontWeight: 'bold'}}>NUMĂR STIVE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="left">{row.shopName}</TableCell>
                  <TableCell align="center">
                    <Input
                      type="number"
                      value={row.confirmedStackNumber}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        formik.setFieldValue(`stackList[${row.id}].confirmedStackNumber`, newValue);
                      }}
                      variant="standard"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>,
        <Button
          style={{ width: '100%' }}
          type="button"
          variant="contained"
          label="PASUL URMĂTOR"
          onClick={() => formik.handleSubmit()}
          disabled={formik.isSubmitting}
        />,
        <Button
          style={{ width: '100%' }}
          type="button"
          variant="contained"
          label="INAPOI"
          onClick={() => navigate(URLS.AUTH_IN_PROGRESS_RETURN)}
        />,
        <AlertModal
          open={isInvalidTruckNumberModalOpen}
          handleClose={closeInvalidTruckNumberModal}
          title={InvalidTruckNumberModalContent.title}
          description={InvalidTruckNumberModalContent.description}
          buttons={InvalidTruckNumberModalContent.buttons}
        />
      ]}
      itemsPerRow={1}
    />
  );
};

export default ExpectedReturnDetailStep2;
